<template>
  <ReportPageLayout>
    <template #page-header>
      <OverviewNavHeader
        v-if="report && report.report_grades && report.report_grades.length > 0"
        :report="report"
      />
    </template>

    <!-- Default slot for main content -->
    <div v-if="loading" class="d-flex justify-center align-center py-12">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </div>
    <div v-else-if="error && !report" class="error-container text-center py-8">
      <v-alert
        type="error"
        title="Error Loading Report"
        :text="error"
        class="mx-auto"
        max-width="600"
        variant="tonal"
        border="start"
      ></v-alert>
      <button type="button" class="btn btn-primary mt-4" @click="reloadReport">
        Retry
      </button>
    </div>
    <v-responsive v-else-if="report" max-width="1320" class="mx-auto">
      <div id="top"></div>
      <v-row>
        <v-col cols="12">
          <GradeHeadingCard
            v-if="report"
            :report="report"
            :selected-grade="String(route.params.grade)"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" lg="3">
          <!-- Navigation Sidebar -->
          <div v-if="!loading && report && report.slug">
            <ReportSideNav
              :key="`${route.params.id}-${route.params.grade}`"
              :report="report"
              mode="grade"
              :grade="route.params.grade as string"
              :currentGatewayReport="gradeReport"
              :gradeReport="gradeReport"
            />
          </div>
        </v-col>
        <v-col cols="12" lg="9">
          <div class="grade-overview-section">
            <SeriesPerformanceSnapshot
              :report="report"
              title="Ratings Snapshot"
              class="mb-8"
              id="ratings-snapshot"
            />

            <GradeLevelSummaryRow
              v-if="gradeReport"
              :report="gradeReport"
              :selected-grade="String(route.params.grade)"
              id="gateway-ratings-summary"
            />

            <!-- Grade Level Overview -->
            <div id="grade-level-overview" v-if="gradeReport?.summary">
              <SectionHeading :title="sectionTitle" level="h2">
              </SectionHeading>
              <v-expand-transition>
                <div>
                  <ExpandedContent :html="gradeReport.summary" />
                </div>
              </v-expand-transition>
            </div>

            <!-- Publication Details -->
            <div id="publication-details">
              <PublicationDetails :report="report" :gradeReport="gradeReport" />
            </div>
          </div>
        </v-col>
      </v-row>
    </v-responsive>
    <div v-else class="text-center py-8">
      <v-alert
        type="info"
        title="No Report Data"
        text="No grade overview data was found."
        class="mx-auto"
        max-width="600"
      ></v-alert>
    </div>
  </ReportPageLayout>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useRoute } from 'vue-router';
  import { API_BASE_URL } from '@/config';
  import { useGlossary } from '../composables/useGlossary';
  import {
    useReportLoader,
    useGradeReportLoader,
  } from '../composables/useReportLoader';
  import { useDocumentTitle } from '../composables/useDocumentTitle';
  import ReportPageLayout from '../layouts/ReportPageLayout.vue';
  import ReadMore from '../components/shared/ReadMore.vue';
  import ExpandedContent from '../components/shared/ExpandedContent.vue';
  import ReportSideNav from '../components/shared/ReportSideNav.vue';
  import GradeLevelSummaryRow from '../components/reportGrade/GradeOverviewSummaryRow.vue';
  import SeriesPerformanceSnapshot from '../components/reports/SeriesPerformanceSnapshot.vue';
  import OverviewNavHeader from '../components/reportOverview/OverviewNavHeader.vue';
  import GradeHeadingCard from '../components/reportGrade/GradeHeadingCard.vue';
  import PublicationDetails from '../components/reportGrade/PublicationDetails.vue';
  import type { Report } from '../store';
  import SectionHeading from '../components/shared/SectionHeading.vue';
  import { formatGradeName } from '../lib/grades';

  const route = useRoute();
  const { openGlossary } = useGlossary();

  const reportSlug = computed(() => route.params.id as string);
  const gradeSlug = computed(() => route.params.grade as string);

  // Load general report data for navigation and basic info
  const {
    report,
    loading: reportLoading,
    error: reportError,
    reloadReport: reloadGeneralReport,
  } = useReportLoader(reportSlug);

  // Load grade-specific data for the actual content
  const {
    gradeReport,
    loading: gradeLoading,
    error: gradeError,
    reloadGradeReport,
  } = useGradeReportLoader(reportSlug, gradeSlug);

  // Combined loading and error states
  const loading = computed(() => reportLoading.value || gradeLoading.value);
  const error = computed(() => reportError.value || gradeError.value);

  // Combined reload function
  const reloadReport = () => {
    reloadGeneralReport();
    reloadGradeReport();
  };

  // Computed property for the section heading title
  const sectionTitle = computed(() => {
    const gradeInfo = report.value?.report_grades?.find(
      (grade) => grade.slug === route.params.grade,
    );

    if (gradeInfo && report.value?.subject?.name) {
      const gradeDisplay =
        gradeInfo.label || gradeInfo.title || formatGradeName(gradeInfo.name);
      return `${report.value.subject.name} ${gradeDisplay} Overview`;
    }
    return 'Grade Level Overview';
  });

  // Update document title to match the same format
  const documentTitleState = computed(() => {
    if (loading.value) return 'Loading Report...';
    if (error.value) return 'Error Loading Report';

    const gradeInfo = report.value?.report_grades?.find(
      (grade) => grade.slug === route.params.grade,
    );

    if (report.value?.title && gradeInfo && report.value?.subject?.name) {
      const gradeDisplay =
        gradeInfo.label || gradeInfo.title || formatGradeName(gradeInfo.name);
      return `${report.value.subject.name} ${gradeDisplay} Overview`;
    }
    return 'Grade Overview';
  });

  useDocumentTitle(documentTitleState);
</script>

<style lang="scss" scoped>
  @use '../styles/abstracts/variables' as *;

  .v-responsive {
    padding-right: 0;
  }

  .content-section-title {
    font-family: 'rockwell', sans-serif !important;
    font-size: 38px !important;
    font-weight: normal !important;
  }

  .overview-section {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .content {
    color: rgb(var(--v-theme-text-secondary));
    font-size: 16px;
    line-height: 1.6;

    p {
      margin-bottom: 16px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .performance-table,
  .reading-metrics-table {
    width: 100%;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 4px;

    th {
      background-color: rgb(var(--v-theme-background));
      font-weight: bold;
      padding: 12px 16px;
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
    }

    td {
      padding: 12px 16px;
      border-bottom: 1px solid
        rgba(var(--v-border-color), var(--v-border-opacity));
    }

    tr:last-child td {
      border-bottom: none;
    }

    tr:nth-child(even) {
      background-color: rgb(var(--v-theme-surface));
    }
  }

  .grade-level-summary {
    h4 {
      font-size: 16px;
    }

    .v-card {
      border: 1px solid rgba(var(--v-theme-border-light), 1);
    }
  }
</style>
